import { Fragment, useCallback, useEffect, useState } from 'react';
import { can, formatRial, toFaDigits as faNum } from '@kia/shared';
import { api, errMsg } from '../api/client';
import { rowButton } from '../lib/rowButton';
import { useAuth } from '../auth/AuthProvider';
import type { View } from '../nav/navModel';
import { Button, Card, ErrorRetry, Kpi, NumberInput, useToast } from '../ui';
import { AttentionStrip } from './AttentionStrip';
import { DepositsPanel } from './DepositsPanel';
import { LoansPanel } from './LoansPanel';
import { RecurringPanel } from './RecurringPanel';

/**
 * تقویم نقدینگی (CIRCULARITY_360 Phase A) — a running cash balance projected forward from the
 * opening balance through the dated cheque register plus the estimated operating tail. Monthly
 * cockpit by default; click a month to drill into its daily events. The first month the balance
 * goes negative (the financing date) and the trough are called out.
 */

interface CalMonth {
  ym: string;
  inflowExact: number;
  inflowEstimated: number;
  outflowExact: number;
  outflowEstimated: number;
  net: number;
  balanceStart: number;
  balanceEnd: number;
  overdueIncluded: boolean;
}
interface Calendar {
  asOf: string;
  asOfAgeDays: number | null;
  asOfStale: boolean;
  openingBalance: number;
  hasOpeningBalance: boolean;
  months: CalMonth[];
  firstNegative: { ym: string; balance: number } | null;
  trough: { ym: string; balance: number };
  monthlyOpexEstimate: number;
  opexBasis: 'recurring' | 'weighted-average' | 'config-fallback' | 'none';
  exactInCount: number;
  exactOutCount: number;
  beyond: {
    inCount: number;
    outCount: number;
    inflow: number;
    outflow: number;
    estimated: { inCount: number; outCount: number; inflow: number; outflow: number };
  };
  horizonMonths: number;
}
interface Balance {
  as_of: string;
  amount: number;
  note: string;
  set_by: string;
  ts: string;
}
interface Drill {
  ym: string;
  balanceStart: number;
  balanceEnd: number;
  days: {
    date: string;
    inflowExact: number;
    inflowEstimated: number;
    outflowExact: number;
    outflowEstimated: number;
    balance: number;
    label: string;
  }[];
}

/** Gregorian YYYY-MM → a compact Persian-digit label. */
function ymLabel(ym: string): string {
  return ym.replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]!);
}

interface OverdueRow {
  id: number;
  no: string;
  customer_name: string | null;
  worstLabel: string;
  daysLate: number;
  count: number;
  remaining: number;
}

export function CashCalendarScreen({
  onOpenDeal,
  onOpenCustomer,
  onNavigate,
}: {
  // UX-1 «کارهای امروز» strip nav — threaded from AppShell, mirrors DashboardScreen's callbacks so an
  // attention row routes to the record / view identically on the cash landing.
  onOpenDeal?: ((id: number) => void) | undefined;
  onOpenCustomer?: ((customerId: number) => void) | undefined;
  onNavigate?: ((view: View, focusDealId?: number) => void) | undefined;
} = {}) {
  const { user } = useAuth();
  const toast = useToast();
  const canWrite = user ? can(user.role, 'cashflow.write') : false;
  const [cal, setCal] = useState<Calendar | null>(null);
  const [bal, setBal] = useState<Balance | null>(null);
  const [amount, setAmount] = useState<number>(0);
  const [note, setNote] = useState('');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openMonth, setOpenMonth] = useState<string | null>(null);
  const [drill, setDrill] = useState<Drill | null>(null);
  const [tab, setTab] = useState<'calendar' | 'loans' | 'recurring' | 'deposits'>('calendar');
  const [loadError, setLoadError] = useState<string | null>(null);
  // The overdue-receivable worklist on the Finance landing (TKT-7) — who to chase today, on this screen.
  const [overdue, setOverdue] = useState<OverdueRow[]>([]);
  // TKT-11 Tier-3c — an AI-drafted collection message for an overdue customer (review + send manually).
  const canAi = user ? can(user.role, 'ai.use') : false;
  const [dunning, setDunning] = useState<{ customer: string; text: string } | null>(null);
  const [dunningFor, setDunningFor] = useState('');
  async function draftDunning(customer: string) {
    setDunningFor(customer);
    const r = await api<{ draft?: string; reply?: string }>('/api/ai/dunning', {
      method: 'POST',
      body: JSON.stringify({ customer }),
    });
    setDunningFor('');
    if (!r.ok) return toast(errMsg(r), 'error');
    const b = r.body as { draft?: string; reply?: string };
    if (b.draft) setDunning({ customer, text: b.draft });
    else toast(b.reply ?? 'پیش‌نویسی تولید نشد', 'error');
  }
  // TKT-11 Tier-3d — the AI cash plan when a shortfall is forecast (which receivables to chase, which
  // payables to defer, how much bridge financing by when). The owner reviews and acts.
  const [cashPlanText, setCashPlanText] = useState('');
  const [cashPlanBusy, setCashPlanBusy] = useState(false);
  async function makeCashPlan() {
    setCashPlanBusy(true);
    const r = await api<{ plan?: string; reply?: string }>('/api/ai/cash-plan', {
      method: 'POST',
      body: '{}',
    });
    setCashPlanBusy(false);
    if (!r.ok) return toast(errMsg(r), 'error');
    const b = r.body as { plan?: string; reply?: string };
    if (b.plan) setCashPlanText(b.plan);
    else toast(b.reply ?? 'برنامه‌ای تولید نشد', 'error');
  }

  const reload = useCallback(() => {
    setLoadError(null);
    void api<Calendar>('/api/cash/calendar').then((r) => {
      if (r.ok) setCal(r.body as Calendar);
      else
        setLoadError(
          (r.body as { message?: string } | undefined)?.message ?? 'خطا در بارگذاری تقویم نقدینگی',
        );
    });
    void api<{ balance: Balance | null }>('/api/cash/balance').then((r) => {
      if (r.ok) setBal((r.body as { balance: Balance | null }).balance);
    });
    void api<{ overdue: OverdueRow[] }>('/api/cash/overdue-tranches').then((r) => {
      if (r.ok) setOverdue((r.body as { overdue: OverdueRow[] }).overdue);
    });
  }, []);
  useEffect(() => {
    reload();
  }, [reload]);

  async function saveBalance() {
    setBusy(true);
    const r = await api('/api/cash/balance', {
      method: 'POST',
      body: JSON.stringify({ amount, note }),
    });
    setBusy(false);
    if (!r.ok) {
      toast((r.body as { message?: string }).message ?? 'خطا در ثبت', 'error');
      return;
    }
    toast('موجودی اولیه ثبت شد ✓');
    setEditing(false);
    setNote('');
    reload();
  }

  async function toggleMonth(ym: string) {
    if (openMonth === ym) {
      setOpenMonth(null);
      setDrill(null);
      return;
    }
    setOpenMonth(ym);
    setDrill(null);
    const r = await api<Drill>(`/api/cash/calendar/day?ym=${ym}`);
    if (r.ok) setDrill(r.body as Drill);
  }

  const opexNote = !cal
    ? ''
    : cal.opexBasis === 'recurring'
      ? 'هزینه‌های ثابت از «تعهدات دوره‌ای» روی روز پرداختشان اعمال می‌شوند (نه میانگین کلی).'
      : cal.opexBasis === 'weighted-average'
        ? `برآورد هزینهٔ ماهانه از میانگین وزنی بستن ماه‌های اخیر: ${formatRial(cal.monthlyOpexEstimate)}`
        : cal.opexBasis === 'config-fallback'
          ? `برآورد هزینهٔ ماهانه از اهرم «هزینهٔ عمومی»: ${formatRial(cal.monthlyOpexEstimate)}`
          : 'تخمین هزینهٔ عملیاتی هنوز صفر است (تاریخچهٔ بستن ماه یا اهرم هزینه وارد نشده).';

  return (
    <>
      {/* UX-1: the money-ranked «کارهای امروز» worklist on the cash landing — same reusable strip as the
          dashboard + deals, collapsible so it never shoves the calendar down. */}
      <AttentionStrip
        variant="collapsible"
        onOpenDeal={onOpenDeal}
        onOpenCustomer={onOpenCustomer}
        onNavigate={onNavigate}
      />
      <Card>
        <div className="row" style={{ gap: 8 }}>
          <Button
            variant={tab === 'calendar' ? 'primary' : 'secondary'}
            onClick={() => setTab('calendar')}
          >
            تقویم نقدینگی
          </Button>
          <Button
            variant={tab === 'loans' ? 'primary' : 'secondary'}
            onClick={() => setTab('loans')}
          >
            وام‌های بانکی
          </Button>
          <Button
            variant={tab === 'recurring' ? 'primary' : 'secondary'}
            onClick={() => setTab('recurring')}
          >
            هزینه‌های ثابت
          </Button>
          <Button
            variant={tab === 'deposits' ? 'primary' : 'secondary'}
            onClick={() => setTab('deposits')}
          >
            پیش‌دریافت‌ها
          </Button>
        </div>
      </Card>

      {tab === 'loans' ? (
        <LoansPanel />
      ) : tab === 'recurring' ? (
        <RecurringPanel />
      ) : tab === 'deposits' ? (
        <DepositsPanel />
      ) : loadError ? (
        <Card>
          <ErrorRetry error={loadError} onRetry={reload} />
        </Card>
      ) : !cal ? (
        <Card>
          <p className="muted">در حال بارگذاری…</p>
        </Card>
      ) : (
        <>
          {overdue.length > 0 ? (
            <Card title={`اقساط عقب‌افتاده — پیگیری وصول (${faNum(overdue.length)})`}>
              <div className="banner banner-warn" style={{ marginTop: 0 }}>
                ماندهٔ کل عقب‌افتاده:{' '}
                <b className="num">{formatRial(overdue.reduce((s, o) => s + o.remaining, 0))}</b>{' '}
                ریال
              </div>
              <table className="table">
                <thead>
                  <tr>
                    <th>قرارداد / مشتری</th>
                    <th>بدترین قسط</th>
                    <th>تأخیر</th>
                    <th>ماندهٔ عقب‌افتاده</th>
                    {canAi ? <th></th> : null}
                  </tr>
                </thead>
                <tbody>
                  {overdue.map((o) => (
                    <tr key={o.id}>
                      <td>
                        {o.no}
                        <div className="muted">{o.customer_name || '—'}</div>
                      </td>
                      <td>{o.worstLabel}</td>
                      <td style={{ color: 'var(--red)', fontWeight: 600 }}>
                        {faNum(o.daysLate)} روز
                        {o.count > 1 ? (
                          <span className="muted"> ({faNum(o.count)} قسط)</span>
                        ) : null}
                      </td>
                      <td className="num">{formatRial(o.remaining)}</td>
                      {canAi ? (
                        <td>
                          {o.customer_name ? (
                            <Button
                              variant="secondary"
                              loading={dunningFor === o.customer_name}
                              disabled={!!dunningFor}
                              onClick={() => void draftDunning(o.customer_name!)}
                            >
                              پیش‌نویس پیام
                            </Button>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
              {dunning ? (
                <div
                  style={{
                    marginTop: 12,
                    padding: 12,
                    border: '1px solid var(--line)',
                    borderRadius: 'var(--radius-xs)',
                  }}
                >
                  <div className="row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
                    <b>پیش‌نویس پیام پیگیری — «{dunning.customer}»</b>
                    <Button variant="secondary" onClick={() => setDunning(null)}>
                      بستن
                    </Button>
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.9 }}>{dunning.text}</div>
                  <p className="muted" style={{ marginTop: 8 }}>
                    این پیش‌نویس را بازبینی و به‌صورت دستی برای مشتری ارسال کنید.
                  </p>
                </div>
              ) : null}
            </Card>
          ) : null}
          <Card title="موجودی نقدی اولیه (لنگر تقویم)">
            <p className="muted" style={{ marginTop: 0 }}>
              نقطهٔ شروع محاسبه: موجودی واقعی بانک + صندوق امروز. تقویم از این عدد به جلو حرکت
              می‌کند.
            </p>
            {cal?.asOfStale ? (
              <p className="warn" style={{ marginTop: 0 }}>
                ⚠ این لنگر {cal.asOfAgeDays} روز پیش ثبت شده — ممکن است پول واقعی از آن زمان جابه‌جا
                شده باشد. برای دقت پیش‌بینی، موجودی امروز را دوباره ثبت کنید.
              </p>
            ) : null}
            <div className="row" style={{ gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <Kpi
                label={
                  bal ? `ثبت‌شده در ${ymLabel(bal.as_of)} توسط ${bal.set_by}` : 'هنوز ثبت نشده'
                }
                value={formatRial(bal?.amount ?? 0)}
                tone={bal ? 'default' : 'bad'}
              />
              {canWrite ? (
                editing ? (
                  <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ width: 200 }}>
                      <NumberInput value={amount} onValue={setAmount} placeholder="مبلغ (﷼)" />
                    </div>
                    <Button onClick={saveBalance} loading={busy}>
                      ثبت
                    </Button>
                    <Button variant="secondary" onClick={() => setEditing(false)} disabled={busy}>
                      انصراف
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setAmount(bal?.amount ?? 0);
                      setEditing(true);
                    }}
                  >
                    {bal ? 'به‌روزرسانی موجودی' : 'ثبت موجودی اولیه'}
                  </Button>
                )
              ) : null}
            </div>
          </Card>

          <Card title="تقویم نقدینگی — مانده در پایان هر ماه">
            <div className="row" style={{ gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
              <Kpi
                label="نخستین ماه کسری نقدی"
                value={cal.firstNegative ? ymLabel(cal.firstNegative.ym) : 'بدون کسری در افق'}
                tone={cal.firstNegative ? 'bad' : 'good'}
              />
              <Kpi
                label={`کمترین مانده (${ymLabel(cal.trough.ym)})`}
                value={formatRial(cal.trough.balance)}
                tone={cal.trough.balance < 0 ? 'bad' : 'default'}
              />
            </div>
            {cal.firstNegative && canAi ? (
              <div style={{ marginBottom: 10 }}>
                <Button
                  variant="secondary"
                  loading={cashPlanBusy}
                  disabled={cashPlanBusy}
                  onClick={() => void makeCashPlan()}
                >
                  برنامهٔ نقدینگی هوش مصنوعی
                </Button>
                {cashPlanText ? (
                  <div
                    style={{
                      marginTop: 10,
                      padding: 12,
                      border: '1px solid var(--line)',
                      borderRadius: 'var(--radius-xs)',
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.9,
                    }}
                  >
                    {cashPlanText}
                  </div>
                ) : null}
              </div>
            ) : null}
            <p className="muted" style={{ marginTop: 0 }}>
              چک‌های دقیق بر اساس سررسید؛ «دریافتنی تخمینی» = پیش‌دریافت‌ها + وصول موردانتظار
              قراردادها؛ «خروجی تخمینی» = هزینه‌های ثابت + پرداختنی موردانتظار به تأمین‌کنندگان (طبق
              مهلت، تا سقف مبلغی که هنوز چک ندارد) — {opexNote}
              {cal.beyond.inCount + cal.beyond.outCount > 0 ? (
                <>
                  {' '}
                  · فراتر از افق (دقیق): دریافتنی {formatRial(cal.beyond.inflow)} / پرداختنی{' '}
                  {formatRial(cal.beyond.outflow)} (در مانده لحاظ نشده).
                </>
              ) : null}
              {cal.beyond.estimated.inCount + cal.beyond.estimated.outCount > 0 ? (
                <>
                  {' '}
                  · فراتر از افق (تخمینی): دریافتنی {formatRial(cal.beyond.estimated.inflow)} /
                  پرداختنی {formatRial(cal.beyond.estimated.outflow)} (در مانده لحاظ نشده).
                </>
              ) : null}
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>ماه</th>
                    <th>دریافتنی (دقیق)</th>
                    <th>دریافتنی تخمینی</th>
                    <th>پرداختنی (دقیق)</th>
                    <th>خروجی تخمینی</th>
                    <th>خالص ماه</th>
                    <th>مانده پایان ماه</th>
                  </tr>
                </thead>
                <tbody>
                  {cal.months.map((m) => {
                    const isNeg = m.balanceEnd < 0;
                    const isFirstNeg = cal.firstNegative?.ym === m.ym;
                    const hasFlow =
                      m.inflowExact || m.inflowEstimated || m.outflowExact || m.outflowEstimated;
                    return (
                      <Fragment key={m.ym}>
                        <tr
                          {...rowButton(() => toggleMonth(m.ym))}
                          style={{
                            cursor: 'pointer',
                            background: isFirstNeg ? 'var(--red-bg, #fde8e8)' : undefined,
                          }}
                        >
                          <td>
                            {openMonth === m.ym ? '▾ ' : '▸ '}
                            {ymLabel(m.ym)}
                            {m.overdueIncluded ? ' (+ سررسیدگذشته)' : ''}
                          </td>
                          <td className="num">{m.inflowExact ? formatRial(m.inflowExact) : '—'}</td>
                          <td className="num muted" style={{ color: 'var(--green)' }}>
                            {m.inflowEstimated ? formatRial(m.inflowEstimated) : '—'}
                          </td>
                          <td className="num">
                            {m.outflowExact ? formatRial(m.outflowExact) : '—'}
                          </td>
                          <td className="num muted">
                            {m.outflowEstimated ? formatRial(m.outflowEstimated) : '—'}
                          </td>
                          <td
                            className="num"
                            style={{ color: m.net < 0 ? 'var(--red)' : undefined }}
                          >
                            {hasFlow ? formatRial(m.net) : '—'}
                          </td>
                          <td
                            className="num"
                            style={{
                              fontWeight: 700,
                              color: isNeg ? 'var(--red)' : 'var(--green)',
                            }}
                          >
                            {formatRial(m.balanceEnd)}
                          </td>
                        </tr>
                        {openMonth === m.ym && drill ? (
                          <tr>
                            <td colSpan={7} style={{ background: 'var(--panel, #fafafa)' }}>
                              {drill.days.length === 0 ? (
                                <span className="muted">رویداد دقیقی در این ماه ثبت نشده.</span>
                              ) : (
                                <table className="table" style={{ margin: 0 }}>
                                  <tbody>
                                    {drill.days.map((d, i) => (
                                      <tr key={i}>
                                        <td>{ymLabel(d.date)}</td>
                                        <td>{d.label}</td>
                                        <td className="num">
                                          {d.inflowExact ? formatRial(d.inflowExact) : ''}
                                          {d.inflowEstimated ? (
                                            <span
                                              className="muted"
                                              style={{ display: 'block', fontSize: '0.85em' }}
                                            >
                                              ~{formatRial(d.inflowEstimated)}
                                            </span>
                                          ) : null}
                                        </td>
                                        <td className="num">
                                          {d.outflowExact || d.outflowEstimated
                                            ? formatRial(-(d.outflowExact + d.outflowEstimated))
                                            : ''}
                                        </td>
                                        <td className="num" style={{ fontWeight: 700 }}>
                                          {formatRial(d.balance)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </>
  );
}
